import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { pushEvent, ANALYTICS_EVENTS } from "../../utils/analytics";
import { fetchWithAuth } from "../../utils/apiHelpers";
import { decodeBase64UrlJson, getOAuthError, getOAuthPayload } from "../../utils/oauthPayload";
import { createSavedBrokerCredentialFromOAuth } from "../../utils/savedSecrets";
import useUser from "../../hooks/useUser";
import "./TradierSuccess.css";

function normalizeTradierAccounts(profilePayload) {
  const p = profilePayload?.profile || profilePayload;
  const accounts =
    p?.profile?.account ||
    p?.profile?.accounts ||
    p?.account ||
    p?.accounts ||
    p?.profile?.account?.account ||
    p?.profile?.accounts?.account ||
    p?.account?.account ||
    p?.accounts?.account;

  if (Array.isArray(accounts)) return accounts;
  if (accounts && typeof accounts === "object") return [accounts];
  return [];
}

function getAccountNumberFromAccount(account) {
  if (!account) return "";
  return String(account.account_number || account.accountNumber || account.number || "");
}

function readBrokerConnectionParams() {
  const saved = sessionStorage.getItem("tradier_oauth_broker_connection_params");
  if (!saved) return null;

  try {
    return JSON.parse(saved);
  } catch (_e) {
    return null;
  }
}

export default function TradierSuccess() {
  const location = useLocation();
  const navigate = useNavigate();
  const { getApiToken, isAuthenticated } = useUser();

  const payload = getOAuthPayload(location);
  const error = getOAuthError(location);
  const decoded = useMemo(() => (payload ? decodeBase64UrlJson(payload) : null), [payload]);

  const brokerConnectionParams = useMemo(() => readBrokerConnectionParams(), []);

  const [profileLoading, setProfileLoading] = useState(false);
  const [profileError, setProfileError] = useState("");
  const [accounts, setAccounts] = useState([]);
  const [selectedAccountNumber, setSelectedAccountNumber] = useState("");

  const [deploying, setDeploying] = useState(false);
  const [encryptionError, setEncryptionError] = useState(null);
  const [payloadCopied, setPayloadCopied] = useState(false);
  const [jsonCopied, setJsonCopied] = useState(false);
  const [showToast, setShowToast] = useState(false);

  // Check if we came from deploy page
  const deployParams = useMemo(() => {
    const saved = sessionStorage.getItem("tradier_oauth_deploy_params");
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (_e) {
        return null;
      }
    }
    return null;
  }, []);

  const isExternalLumibotFlow = !brokerConnectionParams && !deployParams;

  useEffect(() => {
    if (error === "expired") {
      pushEvent({ event: ANALYTICS_EVENTS.OAUTH_TRADIER_EXPIRED, source: window.location.pathname });
    } else if (!payload) {
      pushEvent({ event: ANALYTICS_EVENTS.OAUTH_TRADIER_LOADING, source: window.location.pathname });
    } else {
      pushEvent({ event: ANALYTICS_EVENTS.OAUTH_TRADIER_SUCCESS, source: window.location.pathname });
    }
  }, [payload, error]);

  const loadProfile = useCallback(async () => {
    if (isExternalLumibotFlow) return;
    if (!payload) return;
    setProfileLoading(true);
    setProfileError("");
    try {
      const token = await getApiToken();
      const resp = await fetchWithAuth(token, "/broker_oauth/tradier/profile", {
        method: "POST",
        body: { payload },
      });

      const list = normalizeTradierAccounts(resp?.profile);
      setAccounts(list);

      if (list.length === 1) {
        const accountNumber = getAccountNumberFromAccount(list[0]);
        if (accountNumber) setSelectedAccountNumber(accountNumber);
      }
    } catch (e) {
      console.error("[TradierSuccess] Failed to load Tradier profile", e);
      setProfileError("Unable to load your Tradier accounts. Please try again.");
    } finally {
      setProfileLoading(false);
    }
  }, [isExternalLumibotFlow, payload, getApiToken]);

  useEffect(() => {
    if (!payload) return;
    if (isExternalLumibotFlow) return;
    if (!isAuthenticated) return;
    loadProfile();
  }, [isExternalLumibotFlow, payload, isAuthenticated, loadProfile]);

  const copyValue = async (value) => {
    try {
      await navigator.clipboard.writeText(value);
      return true;
    } catch (_e) {
      const textarea = document.createElement("textarea");
      textarea.value = value;
      textarea.style.position = "fixed";
      textarea.style.opacity = 0;
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      let ok = false;
      try {
        ok = document.execCommand("copy");
      } catch {}
      document.body.removeChild(textarea);
      return ok;
    }
  };

  const showCopiedToast = () => {
    setShowToast(true);
    setTimeout(() => setShowToast(false), 2200);
  };

  const handleCopyJson = async () => {
    if (!decoded) return;
    const ok = await copyValue(JSON.stringify(decoded, null, 2));
    if (ok) {
      setJsonCopied(true);
      showCopiedToast();
    }
    pushEvent({
      event: ANALYTICS_EVENTS.OAUTH_TRADIER_COPY,
      copyType: "json",
      source: window.location.pathname,
    });
  };

  const handleCopyPayload = async () => {
    if (!payload) return;
    const ok = await copyValue(payload);
    if (ok) {
      setPayloadCopied(true);
      showCopiedToast();
    }
    pushEvent({
      event: ANALYTICS_EVENTS.OAUTH_TRADIER_COPY,
      copyType: "encoded_payload",
      source: window.location.pathname,
    });
  };

  const saveBrokerConnectionCredentials = useCallback(async () => {
    if (!payload || !selectedAccountNumber) return;

    const token = await getApiToken();
    await createSavedBrokerCredentialFromOAuth(token, {
      broker: "tradier",
      displayName: brokerConnectionParams?.displayName || undefined,
      paperOrLive: "live",
      externalAccountId: selectedAccountNumber,
      externalAccountLabel: selectedAccountNumber,
      providerMetadata: {
        accountNumber: selectedAccountNumber,
      },
      credentialValues: {
        TRADIER_TOKEN: payload,
        TRADIER_ACCOUNT_NUMBER: selectedAccountNumber,
      },
    });
    sessionStorage.removeItem("tradier_oauth_broker_connection_params");
    sessionStorage.removeItem("tradier_oauth_deploy_params");
  }, [brokerConnectionParams, getApiToken, payload, selectedAccountNumber]);

  // Auto-redirect when launched from /deploy and we have an account selection
  useEffect(() => {
    if (!payload || !selectedAccountNumber || !deployParams) return;

    let cancelled = false;
    const run = async () => {
      setDeploying(true);
      try {
        await saveBrokerConnectionCredentials();
        sessionStorage.removeItem("tradier_oauth_deploy_params");
        if (cancelled) return;

        setTimeout(() => {
          const deployUrl = `/deploy?strategyId=${deployParams.strategyId}&aiStrategyId=${deployParams.aiStrategyId}&aiStrategyRevisionId=${deployParams.aiStrategyRevisionId}`;
          navigate(deployUrl);
        }, 2000);
      } catch (e) {
        console.error("[TradierSuccess] Error saving broker connection:", e);
        setEncryptionError("Failed to save Tradier connection. Please try again.");
        setDeploying(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [payload, selectedAccountNumber, deployParams, navigate, saveBrokerConnectionCredentials]);

  const handleUseForDeployment = async () => {
    if (!payload || !selectedAccountNumber) return;
    setDeploying(true);
    setEncryptionError(null);
    try {
      await saveBrokerConnectionCredentials();
      navigate(brokerConnectionParams?.returnTo || "/deploy", { replace: true });
    } catch (e) {
      console.error("[TradierSuccess] Error saving broker connection:", e);
      setEncryptionError("Failed to save Tradier connection. Please try again.");
      setDeploying(false);
    }
  };

  if (deploying && deployParams) {
    return (
      <div className="tradier-oauth-container">
        <div className="tradier-oauth-headline">Tradier Connected</div>
        <div className="tradier-oauth-subhead">Redirecting you back to deploy your strategy...</div>
        <div className="tradier-oauth-spinner" />
      </div>
    );
  }

  if (error === "expired") {
    return (
      <div className="tradier-oauth-container">
        <div className="tradier-oauth-error">Authorization took too long – please try again.</div>
        <Link to="/deploy" className="tradier-oauth-secondary-btn">
          Back to Deploy
        </Link>
      </div>
    );
  }

  if (!payload) {
    return (
      <div className="tradier-oauth-container">
        <div className="tradier-oauth-headline">Connecting to Tradier…</div>
        <div className="tradier-oauth-spinner" />
      </div>
    );
  }

  if (isExternalLumibotFlow) {
    return (
      <div className="tradier-oauth-container tradier-oauth-external-container">
        <div className="tradier-oauth-headline">Tradier connected</div>
        <div className="tradier-oauth-subhead">Copy this token JSON into your local Tradier token file.</div>

        <div className="tradier-oauth-token-panel">
          <div className="tradier-oauth-token-panel-header">
            <span>Token JSON</span>
            <button className="tradier-oauth-primary-btn" onClick={handleCopyJson} type="button" disabled={!decoded}>
              {jsonCopied ? "Copied" : "Copy JSON"}
            </button>
          </div>
          <pre className="tradier-oauth-json">{decoded ? JSON.stringify(decoded, null, 2) : "Invalid token JSON"}</pre>
          {showToast && <div className="tradier-oauth-copy-toast">Copied.</div>}
        </div>

        <div className="tradier-oauth-account-note">
          Set <code>TRADIER_ACCOUNT_NUMBER</code> to the Tradier account you want LumiBot to use.
        </div>

        <details className="tradier-oauth-details">
          <summary>Encoded payload</summary>
          <div className="tradier-oauth-copy-row">
            <input
              className="tradier-oauth-copy-input"
              value={payload}
              readOnly
              aria-label="Tradier encoded token payload"
              onFocus={(e) => e.target.select()}
              tabIndex={0}
            />
            <button className="tradier-oauth-secondary-btn" onClick={handleCopyPayload} type="button">
              {payloadCopied ? "Copied" : "Copy"}
            </button>
          </div>
        </details>
      </div>
    );
  }

  return (
    <div className="tradier-oauth-container">
      <div className="tradier-oauth-headline">Tradier connected!</div>
      <div className="tradier-oauth-subhead">
        Select an account to {brokerConnectionParams ? "save as a broker connection." : "use for trading, then return to deploy."}
      </div>

      {!isAuthenticated && <div className="tradier-oauth-error">Please sign in to load your Tradier accounts.</div>}

      {profileLoading ? (
        <div className="tradier-oauth-spinner" />
      ) : profileError ? (
        <div className="tradier-oauth-error">
          {profileError}{" "}
          <button type="button" className="tradier-oauth-secondary-btn" onClick={loadProfile}>
            Retry
          </button>
        </div>
      ) : accounts.length > 0 ? (
        <div className="tradier-oauth-card">
          <label className="tradier-oauth-label">Select Account</label>
          <select
            className="tradier-oauth-select"
            value={selectedAccountNumber}
            onChange={(e) => setSelectedAccountNumber(e.target.value)}
          >
            <option value="" disabled>
              Choose an account…
            </option>
            {accounts.map((acc, idx) => {
              const accountNumber = getAccountNumberFromAccount(acc);
              const key = accountNumber || String(idx);
              return (
                <option key={key} value={accountNumber}>
                  {accountNumber || "Unknown Account"}
                </option>
              );
            })}
          </select>

          <div className="tradier-oauth-actions">
            <button
              type="button"
              className="tradier-oauth-primary-btn"
              onClick={handleUseForDeployment}
              disabled={deploying || !selectedAccountNumber}
              title={!selectedAccountNumber ? "Select an account to continue" : ""}
            >
              {deploying ? "Saving..." : brokerConnectionParams ? "Save broker connection" : "Use for Bot Deployment"}
            </button>
            <Link to="/deploy" className="tradier-oauth-secondary-btn">
              Go to Deploy
            </Link>
          </div>
        </div>
      ) : null}

      {encryptionError && <div className="tradier-oauth-error">{encryptionError}</div>}
    </div>
  );
}
